<template>
  <div>
    <vxe-grid
      ref="table"
      size="small"
      row-id="invKey"
      cell-class-name="align-top"
      auto-resize
      resizable
      show-footer
      highlight-hover-row
      :toolbar-config="tableToolbar"
      :data="tableData"
      :columns="tableColumns"
      :loading="loading"
      :height="tableHeight"
      :seq-config="{ startIndex: (tablePage.curPage - 1) * tablePage.pageSize }"
      :footer-method="() => footerData"
      :checkbox-config="{ highlight: true, reserve: true }"
      @checkbox-change="checkboxChange"
      @checkbox-all="checkboxChange"
      @cell-click="cellClick"
    >
      <template v-slot:toolbar_form>
        <slot name="header"></slot>
      </template>
      <template v-slot:pager>
        <slot name="pager"></slot>
      </template>
      <template v-slot:toolbar_btn>
        <slot name="toolbar_btn"></slot>
      </template>
      <template v-slot:defaultImageUrl="{ row }">
        <el-image
          :src="
            row.defaultImageUrl
              ? $osscdn(row.defaultImageUrl)
              : '/image/static/img/nopic.jpg'
          "
          :preview-src-list="[
            row.defaultImageUrl
              ? $osscdn(row.defaultImageUrl)
              : '/image/static/img/nopic.jpg',
          ]"
          style="width:60px; height:60px"
        >
          <div slot="error" class="image-slot">
            <img
              src="/image/static/img/nopic.jpg"
              style="width: 60px; height: 60px"
            />
          </div>
        </el-image>
      </template>
      <template v-slot:displaySku="{ row }">
        <TableLabel
          label="SKU"
          labelKey="table_label_evgSkuNo"
          :value="row.evgSkuNo"
          primary
        />
        <TableLabel
          label="SPU"
          labelKey="table_label_evgSpuNo"
          :value="row.evgSpuNo"
          primary
        />
        <TableLabel
          label="EAN条码"
          labelKey="table_label_eanCode"
          :value="row.eanCode"
          primary
        />
        <TableLabel
          label="BRAND"
          labelKey="table_label_brandName"
          :value="row.brandName"
        />
        <TableLabel :value="row.goodsName" />
        <TableLabel :value="row.goodsNameEn" />
        <div v-if="'Y' == row.isCombine" class="mt-2">
          <el-tag type="warning" size="mini" effect="plain">
            {{ $l('table_label_isCombine') || '组合产品' }}
          </el-tag>
        </div>
      </template>
      <template v-slot:displayWarehouse="{ row }">
        <TableLabel
          label="SKU"
          labelKey="table_label_productSku"
          :value="row.whSkuNo"
          primary
        />
        <TableLabel
          label="BARCODE"
          labelKey="table_label_productBarcode"
          :value="row.whSkuBarcode"
          primary
        />
        <TableLabel :value="row.whProductTitle" />
        <TableLabel :value="row.whProductTitleEn" />
        <TableLabel
          label="仓库代码"
          labelKey="table_label_warehouseCode"
          :value="row.warehouseCode"
        />
        <TableLabel
          label="仓库描述"
          labelKey="table_label_warehouseDesc"
          :value="row.warehouseDesc"
        />
      </template>
      <template v-slot:displayWarehouseInv="{ row }">
        <TableLabel
          class="font-weight-bolder"
          label="渠道可售数量"
          labelKey="table_label_platform_sellable"
          :value="format(row.platformSellable) || '0'"
        />
        <TableLabel
          class="font-weight-bolder"
          label="渠道库存占比"
          labelKey="table_label_platform_ratio"
          :value="format(row.platformRatio) || '0%'"
        />
        <TableLabel
          label="可售数量"
          labelKey="table_label_sellable"
          :value="format(row.sellable) || '0'"
          :gaps="row.quantityGaps"
        />
        <TableLabel
          class="font-weight-bolder"
          v-if="row.isCompleteSet == 'Y' && 'Y' != row.isCombine"
          label="成套数量"
          labelKey="table_label_completeSetQty"
          :value="format(row.completeSetQty) || '0'"
        />
        <TableLabel
          class="font-weight-bolder"
          v-if="row.isCompleteSet == 'Y' && 'Y' != row.isCombine"
          label="剩余数量"
          labelKey="table_label_remaining"
          :value="format(row.sellable - row.completeSetQty) || '0'"
        />
        <TableLabel
          label="待出库数量"
          labelKey="table_label_reserved"
          :value="format(row.reserved) || '0'"
        />
        <TableLabel
          label="历史出库数量"
          labelKey="table_label_shipped"
          :value="format(row.shipped) || '0'"
        />
        <TableLabel
          label="在途数量"
          labelKey="table_label_onway"
          :value="format(row.onway) || '0'"
        />
        <TableLabel
          v-if="row.salesPrice"
          label="可售库存总金额"
          labelKey="table_label_totalSellablePrice"
          :value="
            `${format(row.totalSellablePrice) || '0'}${
              row.v_salesCurrencyUnit ? ' ' + row.v_salesCurrencyUnit : ''
            }`
          "
          primary
        />
        <el-divider class="my-1"></el-divider>
        <TableLabel
          label="库存更新时间"
          labelKey="table_label_piUpdateTime"
          :value="row.piUpdateTime"
        />
        <TableLabel
          label="数据更新时间"
          labelKey="table_label_updateTime"
          :value="row.updateTime"
        />
      </template>
      <template v-slot:displaySellable="{ row }">
        <TableLabel
          label="装箱入数"
          labelKey="table_label_inCtnQty"
          :value="format(row.inCtnQty) || '0'"
        />
        <TableLabel
          label="产品GTIN"
          labelKey="table_label_eachGtinNo"
          :value="row.eachGtinNo"
        />
        <TableLabel
          label="内包装GTIN"
          labelKey="table_label_innerPackGtinNo"
          :value="row.innerPackGtinNo"
        />
        <TableLabel
          label="箱包装GTIN"
          labelKey="table_label_caseGtinNo"
          :value="row.caseGtinNo"
        />
        <TableLabel
          label="最小出货单位"
          labelKey="table_label_sellableEachUnit"
          :value="row.v_sellableEachUnit"
        />
        <TableLabel
          label="最小出货入数"
          labelKey="table_label_sellableEachQty"
          :value="format(row.sellableEachQty) || '0'"
        />
      </template>
      <template v-slot:displaySales="{ row }">
        <TableLabel :value="row.salesTitle" />
        <TableLabel
          v-if="row.salesTitle"
          label="售卖价格"
          labelKey="table_label_salesPrice"
          :value="
            `${format(row.salesPrice) || '0'}${
              row.v_salesCurrencyUnit ? ' ' + row.v_salesCurrencyUnit : ''
            }`
          "
          primary
        />
        <TableLabel
          label="渠道Item号"
          labelKey="table_label_channelItemNo"
          :value="row.channelItemNo"
          primary
        />
        <TableLabel
          label="渠道款号"
          labelKey="table_label_channelSkuNo"
          :value="row.channelSkuNo"
          primary
        />
        <TableLabel
          label="渠道条码号"
          labelKey="table_label_channelEanNo"
          :value="row.channelEanNo"
          primary
        />
      </template>
      <template v-slot:displayPlatform="{ row }">
        <TableLabel :value="row.platformName" />
        <TableLabel
          label="BRAND"
          labelKey="table_label_salesBrandName"
          :value="row.salesBrandName"
        />
        <TableLabel
          label="运营人员"
          labelKey="table_label_operateName"
          :value="row.operateName"
        />
        <div v-if="row.v_evgSellType">
          <span>{{ $l('table_label_evgSellType') || 'METHOD' }}：</span>
          <el-tag
            size="mini"
            :type="
              row.evgSellType === 'B'
                ? ''
                : row.evgSellType === 'C'
                ? 'warning'
                : 'success'
            "
          >
            {{ row.v_evgSellType || '未配置' }}
          </el-tag>
        </div>
      </template>
      <template v-slot:displayScm="{ row }">
        <TableLabel
          label="NC"
          labelKey="table_label_ncNo"
          :value="row.ncNo"
          primary
        />
        <TableLabel :value="row.scmName" />
        <TableLabel
          v-if="row.scmName"
          label="采购单价"
          labelKey="table_label_ftyPrice"
          :value="`${row.ftyPrice || '0'} ${row.v_ftyPriceCurrencyUnit || ''}`"
          primary
        />
        <TableLabel
          label="采购人员"
          labelKey="table_label_buyerName"
          :value="row.buyerName"
          info
        />
      </template>
      <template v-slot:empty="{}">
        <div style="text-align: center">
          <img src="/image/static/img/nodata.png" />
          <p class="no-data">
            {{ $l('INDEX.tips_no_data') || '暂无数据！' }}
          </p>
        </div>
      </template>
    </vxe-grid>
  </div>
</template>
<script>
import TableAutoHeight from '@/mixins/table-auto-height'
import { format } from '@/libs/util.number.js'
import tableColumns from './table-columns'
import TableLabel from './label.vue'
export default {
  mixins: [TableAutoHeight],
  components: { TableLabel },
  props: {
    tableData: { type: Array, default: () => [] },
    tablePage: { type: Object, default: () => {} },
    tableTotal: { type: Object, default: () => {} },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      tableToolbar: {
        slots: { buttons: 'toolbar_form', tools: 'toolbar_btn' },
        refresh: { query: () => this.$emit('reload') },
        zoom: true,
        custom: true,
      },
      tableColumns: tableColumns(this),
      format: (n) => format(n),
    }
  },
  computed: {
    footerData() {
      const totalColumns = this.tableColumns.map((column) => {
        const { isTotalColumn, totalColumnKey, totalFormatter } = column
        if (!isTotalColumn) return ''
        if (!totalColumnKey) return ''
        if (totalFormatter) {
          return totalFormatter({ column, total: this.tableTotal })
        }
        return this.tableTotal[totalColumnKey]
      })
      totalColumns[0] = this.$l('INDEX.table_label_total') || '合计'
      return [totalColumns]
    },
  },
  watch: {
    tableTotal: {
      handler() {
        this.$refs.table.updateFooter()
      },
      deep: true,
    },
  },
  methods: {
    checkboxChange({ records, reserves }) {
      this.$emit('checkbox-change', records.concat(reserves))
    },
    clearCheckboxRow() {
      this.$refs.table.clearCheckboxRow()
      this.$refs.table.clearCheckboxReserve()
      this.$emit('checkbox-change', [])
    },
    cellClick({ row, column }) {
      const { property } = column
      const filterColumns = ['checkbox', 'defaultImageUrl']
      if (filterColumns.includes(property)) return

      const $table = this.$refs.table
      $table.toggleCheckboxRow(row)

      const data = $table
        .getCheckboxRecords(true)
        .concat($table.getCheckboxReserveRecords(true))
      this.$emit('checkbox-change', data)
    },
  },
}
</script>
